import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  RefreshCw,
  XCircle
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTable } from '@/components/ui/data-table'
import { toast } from '@/components/ui/use-toast'
import { config as envConfig } from '@/configs/env.config'
import { IOrganization, TableColumn } from '@/models'
import { useAuth } from '@/providers/AuthContextProvider'
import { usePricingTiers } from '@/providers/PricingTierContextProvider'
import { useQueryGetOrganizations } from '@/services'
import {
  PostHogLastSync,
  PostHogPingResult,
  PostHogSyncProgress,
  PricingTierLookup,
  getLastPostHogSync,
  isPostHogConfigured,
  pingPostHog,
  syncOrganizationsToPostHog
} from '@/services/posthog.service'

const formatTimestamp = (iso: string) => {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

const Section = ({
  title,
  description,
  action,
  children
}: {
  title: string
  description?: React.ReactNode
  action?: React.ReactNode
  children?: React.ReactNode
}) => (
  <div className="rounded-md border border-border p-4 space-y-3">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="text-base font-semibold text-primary">{title}</h2>
        {description && (
          <div className="text-sm text-muted-foreground mt-1">
            {description}
          </div>
        )}
      </div>
      {action}
    </div>
    {children}
  </div>
)

const Stat = ({
  label,
  value,
  hint
}: {
  label: string
  value: React.ReactNode
  hint?: React.ReactNode
}) => (
  <div className="rounded-md border border-border p-4">
    <p className="text-sm text-muted-foreground">{label}</p>
    <div className="text-xl font-semibold text-primary mt-1">{value}</div>
    {hint && <p className="text-xs text-muted-foreground mt-2">{hint}</p>}
  </div>
)

const PostHogSyncPage = () => {
  const { userData } = useAuth()
  const userEmail = userData?.attributes?.email

  const { data: orgs = [], isLoading } = useQueryGetOrganizations()
  const {
    pricingTiers,
    pricingTierAssignments,
    isLoading: isPricingTiersLoading
  } = usePricingTiers()

  const pricingTierByOrgId = useMemo<PricingTierLookup>(() => {
    const tierById = new Map(
      (pricingTiers ?? []).map((t) => [t.id, t.name] as const)
    )
    const lookup: PricingTierLookup = {}

    for (const assignment of pricingTierAssignments ?? []) {
      if (!assignment.organization_id) continue
      lookup[assignment.organization_id] = {
        id: assignment.pricing_tier_id,
        name: assignment.pricing_tier_id
          ? tierById.get(assignment.pricing_tier_id)
          : undefined
      }
    }

    return lookup
  }, [pricingTiers, pricingTierAssignments])

  const pricingTierLookupRef = useRef(pricingTierByOrgId)

  useEffect(() => {
    pricingTierLookupRef.current = pricingTierByOrgId
  }, [pricingTierByOrgId])

  const [lastSync, setLastSync] = useState<PostHogLastSync | null>(() =>
    getLastPostHogSync()
  )
  const [progress, setProgress] = useState<PostHogSyncProgress | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [connection, setConnection] = useState<
    | { state: 'idle' }
    | { state: 'checking' }
    | { state: 'done'; result: PostHogPingResult }
  >({ state: 'idle' })

  const configured = isPostHogConfigured()
  const totalOrgs = orgs.length

  const checkConnection = useCallback(async () => {
    setConnection({ state: 'checking' })
    try {
      const result = await pingPostHog()

      setConnection({ state: 'done', result })
    } catch (error) {
      setConnection({
        state: 'done',
        result: {
          ok: false,
          error: error instanceof Error ? error.message : String(error)
        }
      })
    }
  }, [])

  useEffect(() => {
    if (configured) {
      void checkConnection()
    }
  }, [configured, checkConnection])

  const selectedOrgs = useMemo(() => {
    const selected: IOrganization[] = []

    for (const idx of Object.keys(rowSelection)) {
      if (rowSelection[idx]) {
        const org = orgs[Number(idx)]

        if (org) selected.push(org)
      }
    }

    return selected
  }, [rowSelection, orgs])

  const orgsToSync = selectedOrgs.length > 0 ? selectedOrgs : orgs

  useEffect(() => {
    setLastSync(getLastPostHogSync())
  }, [])

  const columns = useMemo<TableColumn<IOrganization>[]>(
    () => [
      {
        id: 'select',
        size: 30,
        header: () => null,
        cell: ({ row }) => (
          <div className="flex h-4 items-center justify-center">
            <Checkbox
              aria-label="Select row"
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
            />
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        enableGlobalFilter: false,
        enableColumnFilter: false
      },
      {
        id: 'id',
        accessorKey: 'id',
        header: 'ID',
        enableSorting: false,
        filterFn: 'includesString'
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        filterFn: 'includesString'
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        filterFn: 'includesString'
      },
      {
        id: 'pricing_tier_name',
        header: 'Pricing Tier',
        enableSorting: false,
        filterFn: 'includesString',
        accessorFn: (row) =>
          row.id ? pricingTierLookupRef.current[row.id]?.name ?? '' : '',
        cell: ({ row }) => {
          const tier = row.original.id
            ? pricingTierLookupRef.current[row.original.id]
            : undefined

          return tier?.name ?? '—'
        }
      },
      {
        id: 'pricing_tier_id',
        header: 'Pricing Tier ID',
        enableSorting: false,
        filterFn: 'includesString',
        accessorFn: (row) =>
          (row.id ? pricingTierLookupRef.current[row.id]?.id : undefined) ??
          row.pricing_tier_id ??
          '',
        cell: ({ row }) => {
          const tierId =
            (row.original.id
              ? pricingTierLookupRef.current[row.original.id]?.id
              : undefined) ?? row.original.pricing_tier_id

          return tierId ?? '—'
        }
      },
      {
        id: 'subscription_status',
        accessorKey: 'subscription_status',
        header: 'Subscription Status',
        enableSorting: false,
        filterFn: 'includesString',
        cell: ({ row }) => row.original.subscription_status ?? '—'
      },
      {
        id: 'subscription_expiry_date',
        accessorKey: 'subscription_expiry_date',
        header: 'Subscription Expiry',
        enableSorting: false,
        filterFn: 'includesString',
        cell: ({ row }) => row.original.subscription_expiry_date ?? '—'
      },
      {
        id: 'company_size',
        accessorKey: 'company_size',
        header: 'Company Size',
        enableSorting: false,
        filterFn: 'includesString',
        cell: ({ row }) => row.original.company_size ?? '—'
      }
    ],
    []
  )

  const handleSync = async () => {
    setIsSyncing(true)
    setProgress({
      completedBatches: 0,
      totalBatches: 0,
      ok: 0,
      failed: 0
    })

    try {
      const result = await syncOrganizationsToPostHog(orgsToSync, {
        onProgress: setProgress,
        triggeredBy: userEmail,
        pricingTierByOrgId
      })

      setLastSync(getLastPostHogSync())

      if (result.failed > 0) {
        toast({
          title: 'PostHog sync partially failed',
          description: `Synced ${result.ok}, failed ${result.failed}. See errors below.`,
          variant: 'destructive'
        })
      } else {
        toast({
          title: 'PostHog sync complete',
          description: `Synced ${result.ok} organizations.`
        })
      }
    } catch (error) {
      toast({
        title: 'PostHog sync failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const progressPercent =
    progress && progress.totalBatches > 0
      ? Math.round((progress.completedBatches / progress.totalBatches) * 100)
      : 0

  const syncButtonLabel = isSyncing
    ? 'Syncing…'
    : selectedOrgs.length > 0
    ? `Sync ${selectedOrgs.length} selected`
    : `Sync all ${totalOrgs}`

  const renderConnectionPill = () => {
    if (!configured) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground">
          <XCircle className="w-3.5 h-3.5" />
          Not configured
        </span>
      )
    }

    if (connection.state === 'idle' || connection.state === 'checking') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Checking…
        </span>
      )
    }

    if (connection.result.ok) {
      return (
        <button
          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-500 hover:bg-emerald-500/20"
          onClick={checkConnection}
          title="Click to retest connection"
          type="button"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          Connected
        </button>
      )
    }

    return (
      <button
        className="inline-flex items-center gap-1.5 rounded-full border border-red-500/50 bg-red-500/10 px-2.5 py-0.5 text-xs font-medium text-red-500 hover:bg-red-500/20"
        onClick={checkConnection}
        title={connection.result.error}
        type="button"
      >
        <XCircle className="w-3.5 h-3.5" />
        Connection failed
      </button>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-primary">PostHog</h1>
          {renderConnectionPill()}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Sync organizations to PostHog as <code>organization</code> groups so
          feature flag rules can match by <code>$group_key</code> and
          autocomplete the org by name in the flag UI. Customer portal already
          calls <code>client.group(&apos;organization&apos;, orgId)</code> at
          login — this sync adds the readable names + properties to every org in
          one shot.
        </p>
      </div>

      {!configured && (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle>PostHog is not configured for this stage</AlertTitle>
          <AlertDescription>
            Set <code>POSTHOG_PROJECT_TOKEN</code> in the stage config (
            <code>iac/configs/&lt;stage&gt;.js</code>) and redeploy. For local
            testing, set it in <code>public/env-config.js</code>.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-start gap-3 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-400">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">
            Sync history is stored in this browser only
          </p>
          <p className="text-xs opacity-90">
            The <strong>Last synced</strong> timestamp and result below are
            cached in your browser&apos;s <code>localStorage</code> (key{' '}
            <code>posthog-last-sync</code>). They are not shared with other
            staff — a colleague who opens this page will see <em>Never</em>{' '}
            until they sync from their own browser. PostHog itself is the source
            of truth — feature flag rules and the group-key autocomplete in the
            PostHog UI are what reflect the actual sync result.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Stat
          hint="Total orgs available to sync."
          label="Organizations"
          value={isLoading ? '…' : totalOrgs}
        />
        <Stat
          hint={
            lastSync?.triggeredBy
              ? `Triggered by ${lastSync.triggeredBy}`
              : 'No sync recorded on this device.'
          }
          label="Last synced"
          value={lastSync ? formatTimestamp(lastSync.timestamp) : 'Never'}
        />
        <Stat
          hint={
            lastSync
              ? `${lastSync.result.totalBatches} batch${
                  lastSync.result.totalBatches === 1 ? '' : 'es'
                } sent`
              : 'Run a sync to populate.'
          }
          label="Last result"
          value={
            lastSync ? (
              <span className="flex items-center gap-2">
                {lastSync.result.failed === 0 ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    {lastSync.result.ok} synced
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                    {lastSync.result.ok} ok / {lastSync.result.failed} failed
                  </>
                )}
              </span>
            ) : (
              '—'
            )
          }
        />
      </div>

      <Section
        action={
          <Button
            disabled={
              !configured ||
              isLoading ||
              isPricingTiersLoading ||
              isSyncing ||
              totalOrgs === 0
            }
            onClick={handleSync}
            size="sm"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
            />
            {syncButtonLabel}
          </Button>
        }
        description={
          <>
            Pushes organizations as PostHog <code>$groupidentify</code> events.
            Repeat clicks update existing group records (upsert by
            <code>$group_key</code>).{' '}
            <Badge variant="outline">$group_type: organization</Badge>{' '}
            <Badge variant="outline">$group_key: org.id</Badge>{' '}
            <Badge variant="outline">env: {envConfig.STAGE}</Badge>
          </>
        }
        title="Sync to PostHog"
      >
        {(isSyncing || progress) && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-primary">
                {progress
                  ? `Batch ${progress.completedBatches} / ${progress.totalBatches}`
                  : 'Preparing…'}
              </span>
              <span className="text-muted-foreground">
                {progress
                  ? `${progress.ok} ok · ${progress.failed} failed`
                  : ''}
              </span>
            </div>
            <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-secondary transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}
      </Section>

      {lastSync && lastSync.result.errors.length > 0 && (
        <Section title="Errors from last sync">
          <ul className="space-y-1 text-sm font-mono">
            {lastSync.result.errors.map((err, idx) => (
              <li className="text-destructive" key={idx}>
                {err}
              </li>
            ))}
          </ul>
        </Section>
      )}

      <DataTable
        columns={columns}
        data={orgs}
        defaultSorting={[]}
        getRowClassName={() => 'h-[52px]'}
        hideColumnFilters
        isLoading={isLoading}
        onRowSelectionChange={setRowSelection}
        tableConfigName="posthog-orgs"
      />
    </div>
  )
}

export default PostHogSyncPage
